/* Lizenzexperte 2026 – festival.js
   Festival-Design: Girlande unter dem Header und Animations-Ebenen (Schnee, Konfetti,
   Feuerwerk, Herzen, Blüten, Schmetterlinge, Blätter, Fledermäuse …). Wird nur geladen,
   wenn in den Theme-Einstellungen ein Festival aktiv ist. Respektiert „Bewegung reduzieren“
   und pausiert, solange der Tab im Hintergrund ist. */
(() => {
  'use strict';

  const root = document.documentElement;
  const fest = root.dataset.festival;
  if (!fest) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const small = window.matchMedia('(max-width: 749px)').matches;
  const css = getComputedStyle(root);
  const cssVar = (name, fallback) => (css.getPropertyValue(name) || '').trim() || fallback;
  const primary = cssVar('--c-primary', '#00badb');
  const rand = (a, b) => a + Math.random() * (b - a);
  const pick = (list) => list[Math.floor(Math.random() * list.length)];
  const TAU = Math.PI * 2;

  /* Je Festival: Girlande (trim), Animationen auf der Seite (fx) und im Header (ambient) */
  const FEST = {
    winter: { trim: 'icicles', fx: ['snow'], ambient: 'flurry' },
    weihnachten: { trim: 'christmas', fx: ['snow', 'glints'], ambient: 'flurry', glint: '#d8b26a' },
    neujahr: { trim: 'stars', fx: ['confetti', 'fireworks'], colors: ['#d9b45a', '#f1d488', '#c0c6d4', '#e8c873', '#b7c4ff'] },
    valentinstag: { trim: 'hearts', fx: ['hearts'], colors: ['#e5487d', '#ff8fb1', '#ff5c8d', '#f7a6c0'] },
    fasching: { trim: 'bunting', fx: ['confetti'], colors: ['#ff4f8b', '#ffd23f', '#3bceac', '#7b5cff', '#ff8a3d', '#2ec4f1'] },
    fruehling: { trim: 'blossoms', fx: ['petals', 'butterflies'], colors: ['#ffc4d6', '#ffb0c8', '#f9d3df', '#f7a1bd'], wings: ['#f7b6c8', '#bcd9ee', '#f5dd92', '#d9c8ef'] },
    ostern: { trim: 'easter', fx: ['petals', 'butterflies'], colors: ['#ffffff', '#f7b6c8', '#f4c2cc', '#ffd6df', '#f9e3a6'], wings: ['#f4c2cc', '#bcd9ee', '#f5dd92', '#d9c8ef', '#c3dcb4'] },
    sommer: { trim: 'bunting', fx: ['bokeh', 'glints'], glint: '#ffb703', colors: ['#ffb703', '#2ec4f1', '#ff6b6b', '#ffd96b', '#3bceac'] },
    herbst: { trim: 'leaves', fx: ['leaves'], colors: ['#d9661b', '#b5442d', '#f2a541', '#8c3b1c', '#c98a2b'] },
    halloween: { trim: 'spiders', fx: ['bats', 'embers'] },
    black_week: { trim: 'tags', fx: ['percent'], colors: ['#111111', primary] },
    black_friday: { trim: 'tags', fx: ['percent'], colors: ['#111111', '#e0313a'] },
    cyber_monday: { trim: 'neon', fx: ['binary'], colors: ['#12d6f5', '#ff2fb4'] }
  };
  const cfg = FEST[fest];
  if (!cfg) return;
  const colors = cfg.colors || ['#ffffff'];

  /* =====================================================================
     1. Girlande unter dem Header
     ===================================================================== */
  const f1 = (n) => Number(n).toFixed(1);
  const delay = (max = 2.4) => `--d:${f1(rand(0, max))}s;--dur:${f1(rand(2.4, 4.4))}s`;
  const qpt = (x0, cx, x1, cy, t) => {
    const mt = 1 - t;
    return { x: mt * mt * x0 + 2 * mt * t * cx + t * t * x1, y: 2 * mt * t * cy };
  };
  const star = (s) => `M0 ${-s}L${f1(s * 0.28)} ${f1(-s * 0.28)}L${s} 0L${f1(s * 0.28)} ${f1(s * 0.28)}L0 ${s}L${f1(-s * 0.28)} ${f1(s * 0.28)}L${-s} 0L${f1(-s * 0.28)} ${f1(-s * 0.28)}Z`;
  const heartPath = (s) => `M0 ${f1(s * 0.3)}C0 0 ${f1(-s * 0.5)} 0 ${f1(-s * 0.5)} ${f1(s * 0.3)}C${f1(-s * 0.5)} ${f1(s * 0.62)} 0 ${f1(s * 0.8)} 0 ${s}C0 ${f1(s * 0.8)} ${f1(s * 0.5)} ${f1(s * 0.62)} ${f1(s * 0.5)} ${f1(s * 0.3)}C${f1(s * 0.5)} 0 0 0 0 ${f1(s * 0.3)}Z`;

  /* Durchhängende Schnur mit Elementen an den Positionen t (0…1) je Bogen */
  function garland(width, o) {
    const n = Math.max(3, Math.round(width / o.span));
    const span = width / n;
    const cy = o.sag * 2;
    let d = 'M0 0';
    let items = '';
    let k = 0;
    for (let i = 0; i < n; i += 1) {
      const x0 = i * span;
      const x1 = x0 + span;
      const cx = x0 + span / 2;
      d += `Q${f1(cx)} ${cy} ${f1(x1)} 0`;
      o.at.forEach((t) => {
        const mt = 1 - t;
        const p = qpt(x0, cx, x1, cy, t);
        const ang = (Math.atan2(2 * mt * cy - 2 * t * cy, 2 * mt * (cx - x0) + 2 * t * (x1 - cx)) * 180) / Math.PI;
        items += o.item(f1(p.x), f1(p.y), ang, k);
        k += 1;
      });
    }
    return `<path d="${d}" fill="none" stroke="${o.wire}" stroke-width="${o.wireWidth || 1.2}" stroke-linecap="round"/>${items}`;
  }

  /* Bemaltes Osterei (Mitte 0/0) */
  const EGG_COLORS = ['#f4c2cc', '#bcd9ee', '#f5dd92', '#c3dcb4', '#d9c8ef'];
  function egg(c, pattern) {
    const rx = 6.4;
    const ry = 8.2;
    let deco = '';
    if (pattern === 0) {
      deco = '<rect x="-6.1" y="-1.8" width="12.2" height="3.6" fill="#fff" opacity=".9"/><circle cx="-3.4" cy="0" r=".8" fill="#d8b26a"/><circle cx="0" cy="0" r=".8" fill="#d8b26a"/><circle cx="3.4" cy="0" r=".8" fill="#d8b26a"/>';
    } else if (pattern === 1) {
      deco = '<path d="M-6 -1.2l2 -1.8 2 1.8 2 -1.8 2 1.8 2 -1.8 2 1.8" fill="none" stroke="#fff" stroke-width="1.2" stroke-linejoin="round"/><path d="M-5.6 2.6l1.9 -1.6 1.9 1.6 1.9 -1.6 1.9 1.6 1.9 -1.6 1.9 1.6" fill="none" stroke="#fff" stroke-width=".9" stroke-linejoin="round" opacity=".8"/>';
    } else if (pattern === 2) {
      deco = '<circle cx="-2.6" cy="-4" r="1" fill="#fff"/><circle cx="2.4" cy="-2.6" r="1.1" fill="#fff"/><circle cx="-3.4" cy="1.4" r="1.2" fill="#fff"/><circle cx="1.6" cy="2.6" r="1" fill="#fff"/><circle cx="-0.4" cy="-0.6" r=".8" fill="#fff"/><circle cx="3.6" cy="1.8" r=".7" fill="#fff"/>';
    } else {
      deco = '<path d="M-6.3 -2.4C-3 -4.2 3 -4.2 6.3 -2.4" fill="none" stroke="#fff" stroke-width="1.1"/><path d="M-6.3 2.4C-3 4.2 3 4.2 6.3 2.4" fill="none" stroke="#fff" stroke-width="1.1"/><path d="M0 -1.4l1.4 1.4 -1.4 1.4 -1.4 -1.4z" fill="#d8b26a"/>';
    }
    return `<ellipse rx="${rx}" ry="${ry}" fill="${c}"/>${deco}<ellipse rx="${rx}" ry="${ry}" fill="url(#ftEgg)"/>`;
  }

  /* Kleine Blüte (Mitte 0/0) */
  const blossom = (c, r = 2.6) => {
    let out = '';
    for (let i = 0; i < 5; i += 1) {
      const a = (i / 5) * TAU - Math.PI / 2;
      out += `<ellipse cx="${f1(Math.cos(a) * r)}" cy="${f1(Math.sin(a) * r)}" rx="${f1(r * 0.95)}" ry="${f1(r * 0.75)}" transform="rotate(${f1((a * 180) / Math.PI + 90)} ${f1(Math.cos(a) * r)} ${f1(Math.sin(a) * r)})" fill="${c}"/>`;
    }
    return `${out}<circle r="${f1(r * 0.55)}" fill="#f2c14e"/>`;
  };

  const TRIMS = {
    /* Weihnachten: Tannengirlande mit warmweißen Lichtern, Samtschleifen und gläsernen Christbaumkugeln */
    christmas: (w) => {
      const n = Math.max(3, Math.round(w / (small ? 150 : 210)));
      const span = w / n;
      const cy = 26;
      const needles = ['', '', ''];
      let frost = '';
      const circuits = ['', '', ''];
      const hangs = [];
      let baubles = '';
      let bows = '';
      for (let i = 0; i < n; i += 1) {
        const x0 = i * span;
        const x1 = x0 + span;
        const cx = x0 + span / 2;
        const steps = Math.round(span / 1.8);
        for (let j = 0; j <= steps; j += 1) {
          const p = qpt(x0, cx, x1, cy, j / steps);
          for (let k = 0; k < 3; k += 1) {
            const a = rand(0, TAU);
            const len = rand(5, 10);
            const sx = p.x + rand(-1.6, 1.6);
            const sy = p.y + rand(-1.4, 1.8);
            needles[k] += `M${f1(sx)} ${f1(sy)}l${f1(Math.cos(a) * len)} ${f1(Math.sin(a) * len * 0.75)}`;
          }
          if (Math.random() < 0.12) frost += `M${f1(p.x + rand(-5, 5))} ${f1(p.y + rand(-4, -1))}l${f1(rand(-3, 3))} ${f1(rand(-1, 1))}`;
        }
        const k = Math.max(6, Math.round(span / 22));
        for (let j = 1; j < k; j += 1) {
          const p = qpt(x0, cx, x1, cy, (j + rand(-0.2, 0.2)) / k);
          const lx = f1(p.x + rand(-3, 3));
          const ly = f1(p.y + rand(-2, 3));
          circuits[(i + j) % 3] += `<circle cx="${lx}" cy="${ly}" r="5.5" fill="#ffc861" opacity=".7" filter="url(#ftWarm)"/><circle cx="${lx}" cy="${ly}" r="2" fill="#fff7dc"/>`;
        }
        const p = qpt(x0, cx, x1, cy, 0.5);
        const len = [10, 24, 16, 30][i % 4];
        const r = i % 3 === 1 ? 9.5 : 8;
        const g = [0, 1, 2, 0, 3, 1][i % 6];
        const by = len + 3 + r;
        /* Jede Kugel ist eine eigene kleine Ebene: das Schaukeln läuft auf der Grafikkarte, die Girlande muss nicht neu gezeichnet werden */
        const hh = Math.ceil(by + r + 2);
        hangs.push(`<span class="ft-hang" style="left:${f1(p.x - 12)}px;top:${f1(p.y + 2)}px;--d:${f1(rand(0, 3))}s;--dur:${f1(rand(3.8, 5.6))}s">`
          + `<svg width="24" height="${hh}" viewBox="-12 -1 24 ${hh}">`
          + `<line x1="0" y1="0" x2="0" y2="${len}" stroke="#c9a85c" stroke-width=".9"/>`
          + `<rect x="-2.8" y="${len - 0.6}" width="5.6" height="3.8" rx="1" fill="#d8b26a"/>`
          + `<circle cy="${f1(by)}" r="${r}" fill="url(#ftB${g})"/>`
          + `<ellipse cx="${f1(-r * 0.36)}" cy="${f1(by - r * 0.38)}" rx="${f1(r * 0.3)}" ry="${f1(r * 0.17)}" fill="#fff" opacity=".75" transform="rotate(-35 ${f1(-r * 0.36)} ${f1(by - r * 0.38)})"/>`
          + `<circle cx="${f1(r * 0.42)}" cy="${f1(by + r * 0.4)}" r="${f1(r * 0.12)}" fill="#fff" opacity=".35"/>`
          + '</svg></span>');
        /* kleine Goldkugeln seitlich */
        [0.27, 0.73].forEach((t, s) => {
          const q = qpt(x0, cx, x1, cy, t);
          const l2 = (i + s) % 2 ? 6 : 11;
          baubles += `<g transform="translate(${f1(q.x)} ${f1(q.y + 3)})"><line x1="0" y1="0" x2="0" y2="${l2}" stroke="#c9a85c" stroke-width=".7"/><circle cy="${l2 + 4}" r="4" fill="url(#ftB${s ? 0 : 3})"/></g>`;
        });
      }
      for (let i = 0; i <= n; i += 1) {
        bows += `<g transform="translate(${f1(i * span)} 2)">`
          + '<path d="M-1 1L-7 15L-4 13.6L-2.4 16.4Z" fill="#8f1622"/><path d="M1 1L7 15L4 13.6L2.4 16.4Z" fill="#8f1622"/>'
          + '<path d="M0 0C-4 -7 -13 -7 -12 0C-13 7 -4 6 0 0Z" fill="#b71f2e" stroke="#d8b26a" stroke-width=".7"/>'
          + '<path d="M0 0C4 -7 13 -7 12 0C13 7 4 6 0 0Z" fill="#b71f2e" stroke="#d8b26a" stroke-width=".7"/>'
          + '<path d="M-9 -1C-7 -3 -4 -3 -2 -1" fill="none" stroke="#ff8a95" stroke-width=".9" opacity=".6"/>'
          + '<circle r="2.8" fill="#d02a3c" stroke="#d8b26a" stroke-width=".7"/></g>';
      }
      const nd = ['#0a2a1f', '#165238', '#2f7752'].map((c, i) => `<path d="${needles[i]}" fill="none" stroke="${c}" stroke-width="${i ? 1.2 : 1.5}" stroke-linecap="round"/>`).join('');
      return {
        base: `${nd}<path d="${frost}" fill="none" stroke="#fff" stroke-width="1.3" stroke-linecap="round" opacity=".85"/>${baubles}`,
        hangs,
        lights: circuits,
        top: bows
      };
    },

    /* Ostern: Frühlingsgirlande aus Blättern, Blüten und Weidenkätzchen mit bemalten Eiern an Seidenschleifen */
    easter: (w) => {
      const n = Math.max(3, Math.round(w / (small ? 150 : 210)));
      const span = w / n;
      const cy = 24;
      const leaves = ['', '', ''];
      let flowers = '';
      let catkins = '';
      let eggs = '';
      let bows = '';
      const leaf = (x, y, ang, len, wd) => {
        const tx = x + Math.cos(ang) * len;
        const ty = y + Math.sin(ang) * len;
        const mx = (x + tx) / 2;
        const my = (y + ty) / 2;
        const nx = -Math.sin(ang) * wd;
        const ny = Math.cos(ang) * wd;
        return `M${f1(x)} ${f1(y)}Q${f1(mx + nx)} ${f1(my + ny)} ${f1(tx)} ${f1(ty)}Q${f1(mx - nx)} ${f1(my - ny)} ${f1(x)} ${f1(y)}Z`;
      };
      const RIBBONS = ['#e9a3b1', '#8fbfdf', '#e3c25d', '#9fc488', '#b9a3df'];
      for (let i = 0; i < n; i += 1) {
        const x0 = i * span;
        const x1 = x0 + span;
        const cx = x0 + span / 2;
        const steps = Math.round(span / 4.2);
        for (let j = 0; j <= steps; j += 1) {
          const p = qpt(x0, cx, x1, cy, j / steps);
          for (let k = 0; k < 2; k += 1) {
            leaves[(j + k) % 3] += leaf(p.x + rand(-2, 2), p.y + rand(-1.5, 1.5), rand(0, TAU), rand(6, 10), rand(2.2, 3.2));
          }
        }
        const fl = Math.max(5, Math.round(span / 30));
        for (let j = 0; j < fl; j += 1) {
          const p = qpt(x0, cx, x1, cy, (j + 0.5 + rand(-0.25, 0.25)) / fl);
          const c = pick(['#ffffff', '#ffffff', '#f6cfd6', '#f9dfa0', '#e7dcf6']);
          flowers += `<g transform="translate(${f1(p.x + rand(-3, 3))} ${f1(p.y + rand(-3, 3))}) rotate(${f1(rand(0, 72))})">${blossom(c, rand(2.8, 3.6))}</g>`;
          if (Math.random() < 0.6) {
            const q = qpt(x0, cx, x1, cy, (j + rand(0, 1)) / fl);
            const cxk = f1(q.x + rand(-4, 4));
            catkins += `<ellipse cx="${cxk}" cy="${f1(q.y + rand(-5, -2))}" rx="2.3" ry="3.6" fill="#f1ede6" stroke="#cdc3b2" stroke-width=".5" transform="rotate(${f1(rand(-30, 30))} ${cxk} ${f1(q.y - 3)})"/>`;
          }
        }
        const p = qpt(x0, cx, x1, cy, 0.5);
        const len = [10, 24, 16, 30][i % 4];
        const rib = RIBBONS[i % RIBBONS.length];
        eggs += `<g transform="translate(${f1(p.x)} ${f1(p.y + 3)})"><g class="ft-sway" style="--d:${f1(rand(0, 3))}s;--dur:${f1(rand(3.6, 5.4))}s">`
          + `<line x1="0" y1="0" x2="0" y2="${len}" stroke="${rib}" stroke-width="1.3"/>`
          + `<g transform="translate(0 ${f1(len + 11)}) rotate(${f1(rand(-8, 8))}) scale(1.4)">${egg(EGG_COLORS[i % EGG_COLORS.length], i % 4)}</g>`
          + '</g></g>';
        [0.27, 0.73].forEach((t, s) => {
          const q = qpt(x0, cx, x1, cy, t);
          const l2 = (i + s) % 2 ? 5 : 11;
          eggs += `<g transform="translate(${f1(q.x)} ${f1(q.y + 3)})"><g class="ft-sway" style="--d:${f1(rand(0, 3))}s;--dur:${f1(rand(3.6, 5.4))}s"><line x1="0" y1="0" x2="0" y2="${l2}" stroke="${RIBBONS[(i + s + 2) % 5]}" stroke-width="1"/><g transform="translate(0 ${l2 + 6}) scale(.8)">${egg(EGG_COLORS[(i * 2 + s + 2) % EGG_COLORS.length], (i + s + 1) % 4)}</g></g></g>`;
        });
      }
      for (let i = 0; i <= n; i += 1) {
        const rib = RIBBONS[(i + 3) % RIBBONS.length];
        bows += `<g transform="translate(${f1(i * span)} 2)">`
          + `<path d="M-1 1C-3 6 -6 10 -8 15L-5 13.8L-3 16.6C-2 11 -1 6 1 1Z" fill="${rib}"/><path d="M1 1C3 6 6 10 8 15L5 13.8L3 16.6C2 11 1 6 -1 1Z" fill="${rib}"/>`
          + `<path d="M0 0C-4 -7 -13 -7 -12 0C-13 7 -4 6 0 0Z" fill="${rib}"/><path d="M0 0C4 -7 13 -7 12 0C13 7 4 6 0 0Z" fill="${rib}"/>`
          + '<path d="M-9 -1C-7 -3 -4 -3 -2 -1M9 -1C7 -3 4 -3 2 -1" fill="none" stroke="#fff" stroke-width="1" opacity=".55"/>'
          + `<circle r="2.8" fill="${rib}" stroke="#fff" stroke-width=".8" stroke-opacity=".6"/></g>`;
      }
      const lv = ['#6f9f5f', '#8bb878', '#a9cf95'].map((c, i) => `<path d="${leaves[i]}" fill="${c}"/>`).join('');
      return `${lv}${catkins}${eggs}${flowers}${bows}`;
    },

    stars: (w) => garland(w, {
      span: small ? 90 : 120, sag: 8, at: [0.22, 0.5, 0.78], wire: '#c9a64a', wireWidth: 1,
      item: (x, y, a, k) => {
        const len = [6, 14, 9][k % 3];
        const s = k % 3 === 1 ? 6.5 : 5;
        const c = k % 2 ? '#d9b45a' : '#c0c6d4';
        return `<g transform="translate(${x} ${y})"><g class="ft-sway" style="${delay()}"><line x1="0" y1="0" x2="0" y2="${len}" stroke="#c9a64a" stroke-width=".8"/><g class="ft-twinkle" style="${delay()}"><path transform="translate(0 ${len + s})" d="${star(s)}" fill="${c}"/></g></g></g>`;
      }
    }),
    hearts: (w) => garland(w, {
      span: small ? 84 : 110, sag: 8, at: [0.3, 0.7], wire: '#c46a8a', wireWidth: 1,
      item: (x, y, a, k) => {
        const len = k % 2 ? 9 : 5;
        const c = colors[k % colors.length];
        return `<g transform="translate(${x} ${y})"><g class="ft-sway" style="${delay()}"><line x1="0" y1="0" x2="0" y2="${len}" stroke="#c46a8a" stroke-width=".8"/><path transform="translate(0 ${len - 1})" d="${heartPath(12)}" fill="${c}"/></g></g>`;
      }
    }),
    bunting: (w) => garland(w, {
      span: small ? 96 : 128, sag: 8, at: [0.1, 0.3, 0.5, 0.7, 0.9], wire: '#8a8a8a', wireWidth: 1,
      item: (x, y, a, k) => `<g transform="translate(${x} ${y}) rotate(${f1(a)})"><g class="ft-sway" style="${delay()};--dur:${f1(rand(1.8, 3))}s"><path d="M-7 0L7 0L0 13Z" fill="${colors[k % colors.length]}"/></g></g>`
    }),
    leaves: (w) => garland(w, {
      span: small ? 90 : 118, sag: 7, at: [0.2, 0.5, 0.8], wire: '#7a5236', wireWidth: 1.6,
      item: (x, y, a, k) => {
        const c = colors[k % colors.length];
        const r = f1(rand(-35, 35));
        return `<g transform="translate(${x} ${y})"><g class="ft-sway" style="${delay()}"><g transform="rotate(${r})"><path d="M0 0C4.5 3 5.5 9 0 15C-5.5 9-4.5 3 0 0Z" fill="${c}"/><path d="M0 1V14" stroke="rgba(0,0,0,.22)" stroke-width=".8"/></g></g></g>`;
      }
    }),
    blossoms: (w) => garland(w, {
      span: small ? 90 : 120, sag: 6, at: [0.18, 0.42, 0.66, 0.88], wire: '#6e5a4a', wireWidth: 1.6,
      item: (x, y, a, k) => `<g transform="translate(${x} ${y})"><g class="ft-twinkle" style="${delay(3)};--dur:${f1(rand(3, 5))}s">${blossom(colors[k % colors.length], 3.2)}</g></g>`
    }),
    tags: (w) => garland(w, {
      span: small ? 96 : 140, sag: 7, at: [0.5], wire: '#3a3a3a', wireWidth: 1,
      item: (x, y, a, k) => {
        const c = colors[k % colors.length];
        const len = k % 2 ? 10 : 5;
        return `<g transform="translate(${x} ${y})"><g class="ft-sway" style="${delay()}"><line x1="0" y1="0" x2="0" y2="${len}" stroke="#3a3a3a" stroke-width=".8"/><g transform="translate(0 ${len})"><path d="M0 0L7 5V20A2 2 0 0 1 5 22H-5A2 2 0 0 1-7 20V5Z" fill="${c}"/><circle cx="0" cy="5" r="1.4" fill="#fff"/><text x="0" y="17" text-anchor="middle" font-size="8.5" font-weight="800" font-family="Arial, sans-serif" fill="#fff">%</text></g></g></g>`;
      }
    }),
    spiders: (w) => {
      let out = '';
      const step = small ? 130 : 190;
      for (let x = step * 0.6; x < w - 60; x += step) {
        const px = f1(x + rand(-30, 30));
        const len = Number(f1(rand(10, 34)));
        let legs = '';
        [-1, 1].forEach((sd) => {
          for (let i = 0; i < 4; i += 1) {
            const yy = len + 2 + i * 1.6;
            legs += `<path d="M0 ${f1(yy)}q${sd * 4} ${f1(-3 + i)} ${sd * 6.5} ${f1(1 + i * 1.2)}" fill="none" stroke="#1b0f2b" stroke-width=".9" stroke-linecap="round"/>`;
          }
        });
        out += `<g transform="translate(${px} 0)"><g class="ft-sway" style="${delay()};--dur:${f1(rand(3.5, 6))}s"><line x1="0" y1="0" x2="0" y2="${len}" stroke="rgba(27,15,43,.55)" stroke-width=".7"/>${legs}<ellipse cx="0" cy="${f1(len + 4.5)}" rx="3.4" ry="4.2" fill="#1b0f2b"/><circle cx="0" cy="${f1(len + 0.4)}" r="2.2" fill="#1b0f2b"/><circle cx="-.8" cy="${f1(len + 0.1)}" r=".5" fill="#ff9a3c"/><circle cx=".8" cy="${f1(len + 0.1)}" r=".5" fill="#ff9a3c"/></g></g>`;
      }
      const r = small ? 40 : 56;
      let web = '';
      for (let i = 0; i <= 5; i += 1) {
        const ang = Math.PI / 2 + (i / 5) * (Math.PI / 2);
        web += `<line x1="0" y1="0" x2="${f1(Math.cos(ang) * r)}" y2="${f1(Math.sin(ang) * r)}"/>`;
      }
      [0.35, 0.62, 0.9].forEach((q) => {
        let d = '';
        for (let i = 0; i <= 5; i += 1) {
          const ang = Math.PI / 2 + (i / 5) * (Math.PI / 2);
          d += `${i ? 'L' : 'M'}${f1(Math.cos(ang) * r * q)} ${f1(Math.sin(ang) * r * q)}`;
        }
        web += `<path d="${d}"/>`;
      });
      return `${out}<g transform="translate(${w} 0)" fill="none" stroke="rgba(27,15,43,.32)" stroke-width=".8">${web}</g>`;
    },
    icicles: (w) => {
      let out = '';
      let x = 2;
      while (x < w) {
        const width = rand(4, 8);
        const len = Math.random() < 0.18 ? rand(16, 26) : rand(5, 13);
        out += `<path d="M${f1(x - width / 2)} 0L${f1(x + width / 2)} 0L${f1(x + 0.4)} ${f1(len)}Z" fill="url(#ftIce)" stroke="rgba(120,170,210,.45)" stroke-width=".5"/>`;
        if (len > 15) out += `<circle class="ft-drip" style="${delay(5)}" cx="${f1(x + 0.4)}" cy="${f1(len + 1)}" r="1.3" fill="#8cc6ec"/>`;
        x += rand(8, 16);
      }
      return `<path d="M0 -3${Array.from({ length: Math.ceil(w / 24) }, (_, i) => `Q${i * 24 + 12} ${i % 2 ? 4 : 2.5} ${i * 24 + 24} -1`).join('')}V-6H0Z" fill="#fff"/>${out}`;
    },
    neon: () => ''
  };

  const DEFS = '<filter id="ftGlow" x="-1" y="-1" width="3" height="3"><feGaussianBlur stdDeviation="3"/></filter>'
    + '<filter id="ftWarm" x="-2" y="-2" width="5" height="5"><feGaussianBlur stdDeviation="2.2"/></filter>'
    + '<linearGradient id="ftIce" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stop-color="#ffffff"/><stop offset="1" stop-color="#a9d6f5"/></linearGradient>'
    + [['#fff3cf', '#d8b26a', '#7e5f1f'], ['#ff9aa4', '#b71f2e', '#55080f'], ['#9be0bf', '#1f7a57', '#08301f'], ['#ffffff', '#d5dbe2', '#7f8893']]
      .map((c, i) => `<radialGradient id="ftB${i}" cx=".36" cy=".3" r=".78"><stop offset="0" stop-color="${c[0]}"/><stop offset=".5" stop-color="${c[1]}"/><stop offset="1" stop-color="${c[2]}"/></radialGradient>`).join('')
    + '<radialGradient id="ftEgg" cx=".34" cy=".28" r=".85"><stop offset="0" stop-color="#fff" stop-opacity=".55"/><stop offset=".45" stop-color="#fff" stop-opacity="0"/><stop offset="1" stop-color="#000" stop-opacity=".14"/></radialGradient>';

  function buildTrim() {
    const header = document.querySelector('.site-header');
    if (!header || header.querySelector('.fest-trim')) return;
    const el = document.createElement('div');
    el.className = `fest-trim fest-trim--${cfg.trim}`;
    el.setAttribute('aria-hidden', 'true');
    header.appendChild(el);
    let lastWidth = 0;
    const draw = () => {
      const w = Math.round(header.clientWidth);
      if (!w || w === lastWidth) return;
      lastWidth = w;
      const body = TRIMS[cfg.trim](w);
      if (!body) { el.innerHTML = ''; return; }
      const svg = (inner, cls = '', style = '') => `<svg xmlns="http://www.w3.org/2000/svg"${cls ? ` class="${cls}"` : ''}${style ? ` style="${style}"` : ''} width="${w}" height="48" viewBox="0 0 ${w} 48">${inner}</svg>`;
      if (typeof body === 'string') { el.innerHTML = svg(`<defs>${DEFS}</defs>${body}`); return; }
      /* In Ebenen: ruhende Girlande einmal gezeichnet, Lichterkreise und Kugeln bewegen sich nur über Deckkraft und Drehung */
      el.innerHTML = svg(`<defs>${DEFS}</defs>${body.base}`)
        + body.hangs.join('')
        + body.lights.map((l, i) => svg(l, 'ft-circuit', `--d:${f1(-rand(0, 3))}s;--dur:${[2.3, 3.1, 3.9][i % 3]}s`)).join('')
        + svg(body.top);
    };
    draw();
    let timer;
    window.addEventListener('resize', () => { clearTimeout(timer); timer = setTimeout(draw, 200); }, { passive: true });
  }

  /* =====================================================================
     2. Animations-Ebenen (Seite + optional Header & Aktionsleiste)
     ===================================================================== */
  const density = Math.min(1, Math.max(0.1, (Number(root.dataset.festDensity) || 50) / 100));
  /* Schwache Geräte: weniger Pixel und höchstens 30 Bilder pro Sekunde – sieht gleich aus, kostet halb so viel */
  const lite = (navigator.hardwareConcurrency || 8) <= 4 || (navigator.deviceMemory || 8) <= 4;

  function makeFX(L) {
    const ctx = L.ctx;
    return {
      /* Schneeflocken mit weichem Schatten – auf hellem und dunklem Grund sichtbar */
      snow: {
        count: 56,
        /* Flocke mit Schatten einmal vorzeichnen und danach nur noch kopieren – Schatten pro Bild sind teuer */
        sprite(crystal) {
          const key = crystal ? 'c' : 'd';
          L.sprites = L.sprites || {};
          if (L.sprites[key]) return L.sprites[key];
          const R = crystal ? 7 : 4.2;
          const size = (R + 6) * 2;
          const c = document.createElement('canvas');
          c.width = c.height = Math.ceil(size * L.dpr);
          const g = c.getContext('2d');
          g.setTransform(L.dpr, 0, 0, L.dpr, size / 2 * L.dpr, size / 2 * L.dpr);
          g.shadowColor = 'rgba(30, 60, 90, .38)';
          g.shadowBlur = 4;
          g.shadowOffsetY = 1;
          if (crystal) {
            g.strokeStyle = '#ffffff';
            g.lineWidth = 1.4;
            g.lineCap = 'round';
            g.beginPath();
            for (let i = 0; i < 3; i += 1) {
              g.rotate(Math.PI / 3);
              g.moveTo(-R, 0); g.lineTo(R, 0);
              const b = R * 0.55;
              const t = R * 0.3;
              g.moveTo(b, 0); g.lineTo(b + t, -t); g.moveTo(b, 0); g.lineTo(b + t, t);
              g.moveTo(-b, 0); g.lineTo(-b - t, -t); g.moveTo(-b, 0); g.lineTo(-b - t, t);
            }
            g.stroke();
          } else {
            g.fillStyle = '#ffffff';
            g.beginPath(); g.arc(0, 0, R, 0, TAU); g.fill();
          }
          L.sprites[key] = { c, R, size };
          return L.sprites[key];
        },
        spawn: (init) => {
          const crystal = Math.random() < 0.24;
          const r = crystal ? rand(5, 9) : rand(1.8, 4.2);
          return { x: rand(0, L.W), y: init ? rand(-L.H * 0.1, L.H) : rand(-40, -10), r, crystal, vy: 14 + r * 6 + rand(0, 12), sway: rand(10, 34), ph: rand(0, TAU), rot: rand(0, TAU), vr: rand(-0.5, 0.5), a: rand(0.8, 1) };
        },
        update: (p, dt) => { p.y += p.vy * dt; p.ph += dt * 0.9; p.rot += p.vr * dt; return p.y > L.H + 12; },
        draw(p) {
          const x = p.x + Math.sin(p.ph) * p.sway;
          const sp = this.sprite(p.crystal);
          const d = sp.size * (p.r / sp.R);
          ctx.globalAlpha = p.a;
          if (p.crystal) {
            ctx.save();
            ctx.translate(x, p.y);
            ctx.rotate(p.rot);
            ctx.drawImage(sp.c, -d / 2, -d / 2, d, d);
            ctx.restore();
          } else {
            ctx.drawImage(sp.c, x - d / 2, p.y - d / 2, d, d);
          }
        }
      },
      /* Feiner Schneefall in Header und Aktionsleiste */
      flurry: {
        area: 4200,
        spawn: (init) => ({ x: rand(0, L.W), y: init ? rand(0, L.H) : -4, r: rand(0.6, 2), vy: rand(9, 24), sway: rand(4, 14), ph: rand(0, TAU), a: rand(0.4, 0.9) }),
        update: (p, dt) => { p.y += p.vy * dt; p.ph += dt; return p.y > L.H + 4; },
        draw: (p) => {
          ctx.globalAlpha = p.a;
          ctx.fillStyle = '#ffffff';
          ctx.beginPath(); ctx.arc(p.x + Math.sin(p.ph) * p.sway, p.y, p.r, 0, TAU); ctx.fill();
        }
      },
      glints: {
        count: 8,
        spawn: () => ({ x: rand(0, L.W), y: rand(L.H * 0.08, L.H * 0.95), t: 0, life: rand(1.6, 3.2), s: rand(3, 6) }),
        update: (p, dt) => { p.t += dt; return p.t > p.life; },
        draw: (p) => {
          const k = Math.sin((Math.PI * p.t) / p.life);
          ctx.globalAlpha = k * 0.85;
          ctx.fillStyle = cfg.glint || '#e3b84a';
          const s = p.s * (0.6 + k * 0.6);
          ctx.beginPath();
          ctx.moveTo(p.x, p.y - s); ctx.lineTo(p.x + s * 0.25, p.y - s * 0.25); ctx.lineTo(p.x + s, p.y);
          ctx.lineTo(p.x + s * 0.25, p.y + s * 0.25); ctx.lineTo(p.x, p.y + s); ctx.lineTo(p.x - s * 0.25, p.y + s * 0.25);
          ctx.lineTo(p.x - s, p.y); ctx.lineTo(p.x - s * 0.25, p.y - s * 0.25); ctx.closePath(); ctx.fill();
        }
      },
      confetti: {
        count: 46,
        spawn: (init) => ({ x: rand(0, L.W), y: init ? rand(-L.H, L.H) : rand(-30, -8), w: rand(4, 7), h: rand(7, 12), c: pick(colors), vy: rand(45, 95), vx: rand(-14, 14), rot: rand(0, TAU), vr: rand(-3, 3), ph: rand(0, TAU), flip: rand(2, 5), a: rand(0.7, 0.95) }),
        update: (p, dt) => { p.y += p.vy * dt; p.x += p.vx * dt; p.ph += p.flip * dt; p.rot += p.vr * dt; return p.y > L.H + 20; },
        draw: (p) => {
          ctx.globalAlpha = p.a;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.rot);
          ctx.scale(1, Math.cos(p.ph));
          ctx.fillStyle = p.c;
          ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
          ctx.restore();
        }
      },
      fireworks: {
        count: 0,
        sparks: [],
        next: 0.8,
        clock: 0,
        tick(dt) {
          this.clock += dt;
          if (this.clock > this.next) {
            this.clock = 0;
            this.next = (rand(2.2, 4.6) / Math.max(0.4, density * 2)) * (small ? 1.5 : 1);
            const cx = rand(L.W * 0.12, L.W * 0.88);
            const cy = rand(L.H * 0.14, L.H * 0.45);
            const c = pick(colors);
            const n = small ? 32 : 46;
            for (let i = 0; i < n; i += 1) {
              const ang = (i / n) * TAU + rand(-0.05, 0.05);
              const sp = rand(70, 170);
              this.sparks.push({ x: cx, y: cy, px: cx, py: cy, vx: Math.cos(ang) * sp, vy: Math.sin(ang) * sp, t: 0, life: rand(1, 1.6), c: Math.random() < 0.25 ? '#ffffff' : c });
            }
          }
          ctx.lineCap = 'round';
          this.sparks = this.sparks.filter((s) => {
            s.t += dt;
            if (s.t > s.life) return false;
            s.px = s.x; s.py = s.y;
            s.vx *= 0.985; s.vy = s.vy * 0.985 + 70 * dt;
            s.x += s.vx * dt; s.y += s.vy * dt;
            ctx.globalAlpha = 1 - s.t / s.life;
            ctx.strokeStyle = s.c;
            ctx.lineWidth = 1.8;
            ctx.beginPath(); ctx.moveTo(s.px, s.py); ctx.lineTo(s.x, s.y); ctx.stroke();
            return true;
          });
        }
      },
      hearts: {
        count: 22,
        spawn: (init) => ({ x: rand(0, L.W), y: init ? rand(0, L.H) : L.H + rand(10, 40), s: rand(8, 15), vy: rand(22, 48), sway: rand(10, 30), ph: rand(0, TAU), c: pick(colors), a: rand(0.35, 0.75), rot: rand(-0.35, 0.35) }),
        update: (p, dt) => { p.y -= p.vy * dt; p.ph += dt * 1.2; return p.y < -24; },
        draw: (p) => {
          const s = p.s;
          ctx.globalAlpha = p.a * Math.min(1, p.y / 140);
          ctx.save();
          ctx.translate(p.x + Math.sin(p.ph) * p.sway, p.y);
          ctx.rotate(p.rot + Math.sin(p.ph) * 0.15);
          ctx.fillStyle = p.c;
          ctx.beginPath();
          ctx.moveTo(0, s * 0.3);
          ctx.bezierCurveTo(0, 0, -s * 0.5, 0, -s * 0.5, s * 0.3);
          ctx.bezierCurveTo(-s * 0.5, s * 0.62, 0, s * 0.8, 0, s);
          ctx.bezierCurveTo(0, s * 0.8, s * 0.5, s * 0.62, s * 0.5, s * 0.3);
          ctx.bezierCurveTo(s * 0.5, 0, 0, 0, 0, s * 0.3);
          ctx.fill();
          ctx.restore();
        }
      },
      /* Blütenblätter, die sanft schweben */
      petals: {
        count: fest === 'ostern' ? 18 : 28,
        spawn: (init) => ({ x: rand(-40, L.W), y: init ? rand(-L.H * 0.2, L.H) : rand(-30, -8), rx: rand(4.5, 7.5), ry: rand(3, 4.6), c: pick(colors), vy: rand(20, 42), vx: rand(8, 24), sway: rand(14, 36), ph: rand(0, TAU), rot: rand(0, TAU), vr: rand(-1.6, 1.6), a: rand(0.75, 0.95) }),
        update: (p, dt) => { p.y += p.vy * dt; p.x += p.vx * dt; p.ph += dt * 1.1; p.rot += p.vr * dt; return p.y > L.H + 16 || p.x > L.W + 40; },
        draw: (p) => {
          ctx.globalAlpha = p.a;
          ctx.save();
          ctx.translate(p.x + Math.sin(p.ph) * p.sway, p.y);
          ctx.rotate(p.rot);
          ctx.scale(Math.cos(p.ph * 1.3), 1);
          ctx.shadowColor = 'rgba(150, 90, 100, .25)';
          ctx.shadowBlur = 3;
          ctx.fillStyle = p.c;
          ctx.beginPath();
          ctx.moveTo(-p.rx, 0);
          ctx.quadraticCurveTo(-p.rx * 0.2, -p.ry * 1.4, p.rx, -p.ry * 0.2);
          ctx.quadraticCurveTo(p.rx * 0.3, p.ry * 1.2, -p.rx, 0);
          ctx.fill();
          ctx.restore();
        }
      },
      /* Schmetterlinge, die über die Seite flattern */
      butterflies: {
        count: 5,
        spawn: (init) => {
          const dir = Math.random() < 0.5 ? 1 : -1;
          return { x: init ? rand(0, L.W) : (dir > 0 ? -30 : L.W + 30), y: rand(L.H * 0.18, L.H * 0.8), dir, vx: rand(32, 58) * dir, ph: rand(0, TAU), flap: rand(11, 15), wob: rand(0, TAU), s: rand(10, 15), c: pick(cfg.wings || colors), a: rand(0.85, 1) };
        },
        update: (p, dt) => { p.x += p.vx * dt; p.ph += p.flap * dt; p.wob += dt * 1.4; p.y += Math.sin(p.wob) * 22 * dt; return p.x < -40 || p.x > L.W + 40; },
        draw: (p) => {
          const s = p.s;
          const f = 0.18 + Math.abs(Math.sin(p.ph)) * 0.82;
          ctx.globalAlpha = p.a;
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(p.dir > 0 ? 0.3 : -0.3);
          [-1, 1].forEach((sd) => {
            ctx.save();
            ctx.scale(sd * f, 1);
            ctx.fillStyle = p.c;
            ctx.strokeStyle = 'rgba(80, 60, 50, .45)';
            ctx.lineWidth = 0.7;
            ctx.beginPath(); ctx.ellipse(s * 0.58, -s * 0.36, s * 0.62, s * 0.46, -0.5, 0, TAU); ctx.fill(); ctx.stroke();
            ctx.beginPath(); ctx.ellipse(s * 0.46, s * 0.32, s * 0.42, s * 0.33, 0.55, 0, TAU); ctx.fill(); ctx.stroke();
            ctx.fillStyle = 'rgba(255,255,255,.8)';
            ctx.beginPath(); ctx.arc(s * 0.72, -s * 0.44, s * 0.11, 0, TAU); ctx.fill();
            ctx.restore();
          });
          ctx.fillStyle = '#4a3b30';
          ctx.beginPath(); ctx.ellipse(0, 0, s * 0.1, s * 0.55, 0, 0, TAU); ctx.fill();
          ctx.strokeStyle = '#4a3b30';
          ctx.lineWidth = 0.6;
          ctx.beginPath(); ctx.moveTo(0, -s * 0.5); ctx.lineTo(-s * 0.22, -s * 0.85); ctx.moveTo(0, -s * 0.5); ctx.lineTo(s * 0.22, -s * 0.85); ctx.stroke();
          ctx.restore();
        }
      },
      bokeh: {
        count: 24,
        spawn: (init) => ({ x: rand(0, L.W), y: init ? rand(0, L.H) : L.H + rand(10, 60), r: rand(4, 14), vy: rand(8, 22), sway: rand(6, 20), ph: rand(0, TAU), c: pick(['255,183,3', '255,217,107', '46,196,241', '255,140,90']), a: rand(0.14, 0.3) }),
        update: (p, dt) => { p.y -= p.vy * dt; p.ph += dt * 0.7; return p.y < -30; },
        draw: (p) => {
          const x = p.x + Math.sin(p.ph) * p.sway;
          const g = ctx.createRadialGradient(x, p.y, 0, x, p.y, p.r);
          g.addColorStop(0, `rgba(${p.c},${p.a})`);
          g.addColorStop(1, `rgba(${p.c},0)`);
          ctx.globalAlpha = 1;
          ctx.fillStyle = g;
          ctx.beginPath(); ctx.arc(x, p.y, p.r, 0, TAU); ctx.fill();
        }
      },
      leaves: {
        count: 22,
        spawn: (init) => ({ x: rand(-20, L.W), y: init ? rand(-L.H * 0.2, L.H) : rand(-30, -10), s: rand(7, 13), c: pick(colors), vy: rand(30, 60), vx: rand(-8, 22), sway: rand(24, 60), ph: rand(0, TAU), rot: rand(0, TAU), vr: rand(-2.4, 2.4), flip: rand(1, 2.6), a: rand(0.72, 0.95) }),
        update: (p, dt) => { p.y += p.vy * dt; p.x += p.vx * dt; p.ph += dt; p.rot += p.vr * dt; return p.y > L.H + 20; },
        draw: (p) => {
          const s = p.s;
          ctx.globalAlpha = p.a;
          ctx.save();
          ctx.translate(p.x + Math.sin(p.ph) * p.sway, p.y);
          ctx.rotate(p.rot);
          ctx.scale(1, Math.cos(p.ph * p.flip));
          ctx.fillStyle = p.c;
          ctx.beginPath();
          ctx.moveTo(0, -s);
          ctx.bezierCurveTo(s * 0.85, -s * 0.5, s * 0.7, s * 0.5, 0, s);
          ctx.bezierCurveTo(-s * 0.7, s * 0.5, -s * 0.85, -s * 0.5, 0, -s);
          ctx.fill();
          ctx.strokeStyle = 'rgba(0,0,0,.18)';
          ctx.lineWidth = 0.8;
          ctx.beginPath(); ctx.moveTo(0, -s * 0.9); ctx.lineTo(0, s * 1.25); ctx.stroke();
          ctx.restore();
        }
      },
      bats: {
        count: 6,
        spawn: (init) => {
          const dir = Math.random() < 0.5 ? 1 : -1;
          return { x: init ? rand(0, L.W) : (dir > 0 ? -50 : L.W + 50), y: rand(L.H * 0.12, L.H * 0.62), dir, vx: rand(55, 115) * dir, s: rand(9, 16), ph: rand(0, TAU), flap: rand(9, 13), bob: rand(6, 18), a: rand(0.55, 0.85) };
        },
        update: (p, dt) => { p.x += p.vx * dt; p.ph += p.flap * dt; return p.x < -70 || p.x > L.W + 70; },
        draw: (p) => {
          const s = p.s;
          const f = Math.sin(p.ph);
          ctx.globalAlpha = p.a;
          ctx.save();
          ctx.translate(p.x, p.y + Math.sin(p.ph * 0.18) * p.bob);
          ctx.fillStyle = '#2a1638';
          [-1, 1].forEach((sd) => {
            ctx.save();
            ctx.scale(sd, 1);
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.quadraticCurveTo(s * 0.5, -s * 0.55 * f - s * 0.15, s * 1.25, -s * 0.45 * f);
            ctx.lineTo(s * 1.0, s * 0.12 - s * 0.2 * f);
            ctx.quadraticCurveTo(s * 0.85, -s * 0.02, s * 0.7, s * 0.14 - s * 0.1 * f);
            ctx.quadraticCurveTo(s * 0.55, s * 0.02, s * 0.4, s * 0.2);
            ctx.closePath();
            ctx.fill();
            ctx.restore();
          });
          ctx.beginPath(); ctx.ellipse(0, s * 0.08, s * 0.2, s * 0.3, 0, 0, TAU); ctx.fill();
          ctx.beginPath(); ctx.moveTo(-s * 0.15, -s * 0.12); ctx.lineTo(-s * 0.1, -s * 0.34); ctx.lineTo(0, -s * 0.16); ctx.lineTo(s * 0.1, -s * 0.34); ctx.lineTo(s * 0.15, -s * 0.12); ctx.fill();
          ctx.restore();
        }
      },
      embers: {
        count: 16,
        spawn: (init) => ({ x: rand(0, L.W), y: init ? rand(L.H * 0.3, L.H) : L.H + 6, r: rand(0.9, 2.2), vy: rand(14, 38), sway: rand(6, 18), ph: rand(0, TAU), a: rand(0.35, 0.8) }),
        update: (p, dt) => { p.y -= p.vy * dt; p.ph += dt * 2; return p.y < L.H * 0.15; },
        draw: (p) => {
          ctx.globalAlpha = p.a * (0.6 + Math.sin(p.ph * 3) * 0.4) * Math.min(1, (p.y - L.H * 0.15) / 120);
          ctx.fillStyle = '#ff9a3c';
          ctx.beginPath(); ctx.arc(p.x + Math.sin(p.ph) * p.sway, p.y, p.r * 2.4, 0, TAU); ctx.fill();
          ctx.globalAlpha *= 0.9;
          ctx.fillStyle = '#ffd08a';
          ctx.beginPath(); ctx.arc(p.x + Math.sin(p.ph) * p.sway, p.y, p.r, 0, TAU); ctx.fill();
        }
      },
      percent: {
        count: 22,
        spawn: (init) => {
          const accent = Math.random() < 0.4;
          return { x: rand(0, L.W), y: init ? rand(-L.H * 0.2, L.H) : rand(-40, -14), size: rand(12, 26), c: accent ? colors[1] : colors[0], a: accent ? rand(0.3, 0.5) : rand(0.08, 0.18), vy: rand(24, 52), sway: rand(6, 20), ph: rand(0, TAU), rot: rand(-0.5, 0.5), vr: rand(-0.7, 0.7) };
        },
        update: (p, dt) => { p.y += p.vy * dt; p.ph += dt; p.rot += p.vr * dt; return p.y > L.H + 30; },
        draw: (p) => {
          ctx.globalAlpha = p.a;
          ctx.save();
          ctx.translate(p.x + Math.sin(p.ph) * p.sway, p.y);
          ctx.rotate(p.rot);
          ctx.fillStyle = p.c;
          ctx.font = `800 ${Math.round(p.size)}px Arial, sans-serif`;
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText('%', 0, 0);
          ctx.restore();
        }
      },
      binary: {
        count: 42,
        spawn: (init) => ({ x: rand(0, L.W), y: init ? rand(-L.H * 0.2, L.H) : rand(-40, -10), size: rand(10, 15), c: pick(colors), a: rand(0.2, 0.5), vy: rand(40, 110), ch: Math.random() < 0.5 ? '0' : '1', sq: Math.random() < 0.25, t: rand(0, 1) }),
        update: (p, dt) => { p.y += p.vy * dt; p.t += dt; if (p.t > 0.6) { p.t = 0; p.ch = p.ch === '0' ? '1' : '0'; } return p.y > L.H + 20; },
        draw: (p) => {
          ctx.globalAlpha = p.a;
          ctx.fillStyle = p.c;
          if (p.sq) {
            ctx.fillRect(p.x, p.y, 3, 3);
          } else {
            ctx.font = `700 ${Math.round(p.size)}px Menlo, Consolas, monospace`;
            ctx.textAlign = 'center';
            ctx.fillText(p.ch, p.x, p.y);
          }
        }
      }
    };
  }

  function startEffects() {
    const layers = [];
    const amount = Math.min(1.25, Math.max(0.45, window.innerWidth / 1440)) * (small ? 0.7 : 1) * density * 2;

    const addLayer = (canvas, sizeOf, names) => {
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const L = { canvas, ctx, W: 0, H: 0, sizeOf };
      const resize = () => {
        const s = sizeOf();
        const dpr = lite ? 1 : Math.min(window.devicePixelRatio || 1, 1.5);
        L.dpr = dpr;
        L.sprites = null;
        L.W = Math.max(1, s.w);
        L.H = Math.max(1, s.h);
        canvas.width = Math.round(L.W * dpr);
        canvas.height = Math.round(L.H * dpr);
        canvas.style.width = `${L.W}px`;
        canvas.style.height = `${L.H}px`;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      };
      resize();
      L.resize = resize;
      const FX = makeFX(L);
      L.systems = names.map((n) => FX[n]).filter(Boolean).map((fx) => {
        const n = fx.area ? Math.round(((L.W * L.H) / fx.area) * density * 2) : Math.round(fx.count * amount);
        const list = [];
        for (let i = 0; i < n; i += 1) list.push(fx.spawn(true));
        return { fx, list };
      });
      layers.push(L);
    };

    /* Seite – entfällt, wenn der Adventskalender seinen eigenen Schnee zeigt (sonst schneit es doppelt und kostet doppelt) */
    if (!document.querySelector('.kal__snow')) {
      const page = document.createElement('canvas');
      page.className = 'fest-canvas';
      page.setAttribute('aria-hidden', 'true');
      document.body.appendChild(page);
      addLayer(page, () => ({ w: window.innerWidth, h: window.innerHeight }), cfg.fx);
    }

    /* Header & Aktionsleiste (z. B. Schneefall im dunklen Header) */
    if (cfg.ambient) {
      ['.site-header', '.deal__bar'].forEach((sel) => {
        const host = document.querySelector(sel);
        if (!host) return;
        const c = document.createElement('canvas');
        c.className = 'fest-ambient';
        c.setAttribute('aria-hidden', 'true');
        host.insertBefore(c, host.firstChild);
        addLayer(c, () => ({ w: host.clientWidth, h: sel === '.site-header' ? (host.querySelector('.header__main') || host).offsetHeight : host.clientHeight }), [cfg.ambient]);
      });
    }

    let timer;
    window.addEventListener('resize', () => { clearTimeout(timer); timer = setTimeout(() => layers.forEach((L) => L.resize()), 150); }, { passive: true });

    let last = performance.now();
    let raf = 0;
    const frame = (now) => {
      if (lite && now - last < 30) { raf = requestAnimationFrame(frame); return; }
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      layers.forEach((L) => {
        const { ctx } = L;
        ctx.clearRect(0, 0, L.W, L.H);
        L.systems.forEach(({ fx, list }) => {
          for (let i = 0; i < list.length; i += 1) {
            if (fx.update(list[i], dt)) list[i] = fx.spawn(false);
            else fx.draw(list[i]);
          }
          if (fx.tick) fx.tick(dt);
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.shadowOffsetY = 0;
        });
        ctx.globalAlpha = 1;
      });
      raf = requestAnimationFrame(frame);
    };
    document.addEventListener('visibilitychange', () => {
      cancelAnimationFrame(raf);
      if (!document.hidden) { last = performance.now(); raf = requestAnimationFrame(frame); }
    });
    raf = requestAnimationFrame(frame);
  }

  if (root.dataset.festTrim === 'true') {
    buildTrim();
    document.addEventListener('shopify:section:load', buildTrim);
  }
  if (root.dataset.festEffects === 'true' && !reduceMotion && (!small || root.dataset.festMobile === 'true')) {
    startEffects();
  }
})();
